import { QueryClient } from '@tanstack/react-query';

const MINUTE = 1000 * 60;

/**
 * 앱 전체가 함께 쓰는 서버 캐시.
 *
 * `index.tsx` 의 `QueryClientProvider` 가 이걸 받는다. 여기로 뺀 이유는
 * **로그아웃·로그인 시점에 캐시를 비워야** 하는데, 그 자리(App·SignInPage)가
 * provider 안쪽이 아니어서 `useQueryClient()` 로는 잡히지 않기 때문이다.
 */
export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1 * MINUTE,
            cacheTime: 5 * MINUTE,
            refetchOnWindowFocus: false,
            enabled: false,
            retry: false,
        },
    },
});

/**
 * 🔴 **계정이 바뀌는 순간 서버 캐시를 통째로 버린다.**
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 필요한가 — 2026-09-03 대표 제보
 * ─────────────────────────────────────────────────────────────
 * 다른 관리자 계정으로 로그인했더니 **직전 센터의 강사 목록**이 그대로 떠 있었다.
 * 다른 센터의 데이터가 보이는 것이라 UX 문제가 아니라 노출 사고다.
 *
 * 원인은 세 가지가 겹친 것이다.
 *   ① 로그아웃도 로그인도 전부 react-router `navigate` 다 — **페이지가 다시
 *      뜨지 않는다.** 그래서 `queryClient` 싱글턴이 계정 전환을 넘어 살아남는다.
 *   ② `clearLocalStorage()` 는 localStorage 만 비운다. 인메모리 캐시는 못 건드린다.
 *   ③ 적잖은 `queryKey` 에 centerId 가 없다. 예를 들어 강사 관리 화면은
 *      `['getTrainerListAPI', 정렬값]` 으로 캐시하면서 centerId 는 **인자로만**
 *      넘긴다. 계정을 바꿔도 키가 같으니 A 센터 응답이 그대로 재사용된다.
 *
 * 키마다 centerId 를 붙이는 것으로는 못 막는다 — 그런 키가 수십 개고, 하나만
 * 빠뜨려도 같은 사고가 난다. **세션이 바뀌는 한 곳에서 통째로 버리는 쪽**이
 * 빠뜨릴 자리가 없다.
 *
 * ─────────────────────────────────────────────────────────────
 * ⚠️ 부르는 자리를 고를 때 — 화면이 아직 떠 있을 때 부르면 안 된다
 * ─────────────────────────────────────────────────────────────
 * `clear()` 는 관찰자가 붙어 있는 쿼리를 지우면 **그 자리에서 다시 부른다.**
 * 로그아웃 버튼을 누른 그 순간은 아직 이전 화면이 마운트돼 있고, 그 화면들이
 * 들고 있는 토큰·centerId 는 **마운트 시점 스냅샷이라 여전히 A 의 것**이다.
 * 그때 비우면 A 의 토큰으로 다시 받아와 **방금 비운 캐시에 A 데이터를 도로
 * 채운다.** 그래서 라우트가 바뀌어 이전 화면이 언마운트된 뒤에 부른다
 * (`/signIn` 진입 시점, 로그인 성공 직후).
 */
export const clearServerCache = () => {
    queryClient.clear();
};
